import { getTrackingProductId } from '@features/products/utils'
import { centAmountToAmountV2 } from '@lib/format-price'
import { graphql } from '@lib/graphql'
import { execute } from '@lib/graphql/execute'
import { CartLineItemsUpdateInput } from '@lib/graphql/graphql'
import { MutationConfig } from '@lib/react-query'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { mapEdgesToItems } from '@utils/map'
import {
  CurrencyCodes,
  pushAddToBasketTrackingEvent,
  pushRemoveFromBasketTrackingEvent,
} from '@utils/tracking-utils'
import { cartsQueryKeys } from '../query-keys'
import { getCartIdFromCookies } from '../utils'

const cartUpdateLineItemsMutationDocument = graphql(`
  mutation CartUpdateLineItems($input: CartLineItemsUpdateInput!) {
    cartLineItemsUpdate(input: $input) {
      cart {
        ...CartDetail
      }
      errors {
        code: __typename
      }
    }
  }
`)

export const cartUpdateLineItems = async (input: CartLineItemsUpdateInput) => {
  return await execute(cartUpdateLineItemsMutationDocument, { input })
}

export async function updateCartItemQuantity({
  lineItemId,
  quantity,
}: {
  lineItemId: string
  quantity: number
}) {
  const cartId = getCartIdFromCookies()

  const cartUpdateLineItemsResponse = await cartUpdateLineItems({
    cartId,
    lineItems: [{ lineItemId: lineItemId, quantity: quantity }],
  })
  return {
    cart: cartUpdateLineItemsResponse?.cartLineItemsUpdate.cart,
    lineItemId,
    quantity,
  }
}

export const useCartUpdateLineItem = (
  mutationConfig?: MutationConfig<typeof updateCartItemQuantity>,
) => {
  const queryClient = useQueryClient()
  const { onSuccess, ...restConfig } = mutationConfig || {}

  return useMutation({
    mutationFn: updateCartItemQuantity,
    onSuccess: (data, variables, context) => {
      const cart = data.cart
      if (!cart) return
      const updatedLineItemId = data.lineItemId
      const previousQuantity = data.quantity
      queryClient.setQueryData(cartsQueryKeys.all, cart)

      const currencyCode = cart.currency as CurrencyCodes
      const lineItem = mapEdgesToItems(cart.lineItems)?.find(
        (item) => item.id === updatedLineItemId,
      )

      if (lineItem) {
        const newQuantity = lineItem.quantity
        if (newQuantity > previousQuantity) {
          pushRemoveFromBasketTrackingEvent({
            currencyCode,
            products: [
              {
                id: getTrackingProductId(
                  lineItem.variant?.product.id,
                  lineItem.variant?.product?.metadata,
                ),
                name: lineItem.variant.product.name,
                variant: lineItem.variant.name,
                category: lineItem.variant.product.tags
                  ?.find((tag) => tag.startsWith('type_'))
                  ?.split('_')[1]
                  .replace(/-/g, ''),
                brand: lineItem.variant.product.vendor,
                quantity: 1,
                price: centAmountToAmountV2(
                  lineItem.unitPrice.discountedPrice
                    ? lineItem.unitPrice.discountedPrice.value
                    : lineItem.unitPrice.value,
                ).toString(),
                dimension1: lineItem.variant.sku,
              },
            ],
            trackingList: 'basket',
          })
        } else {
          pushAddToBasketTrackingEvent({
            currencyCode,
            products: [
              {
                id: getTrackingProductId(
                  lineItem.variant?.product.id,
                  lineItem.variant?.product?.metadata,
                ),
                name: lineItem.variant.product.name,
                variant: lineItem.variant.name,
                category: lineItem.variant.product.tags
                  ?.find((tag) => tag.startsWith('type_'))
                  ?.split('_')[1]
                  .replace(/-/g, ''),
                brand: lineItem.variant.product.vendor,
                quantity: 1,
                price: centAmountToAmountV2(
                  lineItem.unitPrice.discountedPrice
                    ? lineItem.unitPrice.discountedPrice.value
                    : lineItem.unitPrice.value,
                ).toString(),
                dimension1: lineItem.variant.sku,
              },
            ],
            trackingList: 'basket',
          })
        }
      }

      onSuccess?.(data, variables, context)
    },
    ...restConfig,
  })
}
